import { useState, useEffect, useCallback } from 'react';

interface MediaType {
  id: string;
  display_time?: number;
  title?: string;
  [key: string]: any;
}

interface UseLinkedModalSyncProps {
  currentMediaIndex: number;
  filteredMedias: MediaType[];
  isLinkedMode: boolean;
  selectedMediaIds?: string[]; // IDs das mídias selecionadas na promoção
  onModalTimeExpired?: () => void;
  onMediaNotSelected?: () => void; // Callback quando mídia atual não está selecionada
}

interface UseLinkedModalSyncReturn {
  modalDuration: number;
  secondsLeft: number;
  progress: number;
  isActive: boolean;
  currentMediaTitle: string;
}

/**
 * Hook especializado para sincronizar modais de promoção LINKED com a duração das mídias
 * Quando o status da promoção é LINKED, o modal deve:
 * 1. Ignorar o tempo de exibição configurado na promoção
 * 2. Permanecer visível durante toda a duração da mídia atual
 * 3. Fechar automaticamente quando a mídia mudar
 */
export const useLinkedModalSync = ({
  currentMediaIndex,
  filteredMedias,
  isLinkedMode,
  selectedMediaIds = [],
  onModalTimeExpired,
  onMediaNotSelected
}: UseLinkedModalSyncProps): UseLinkedModalSyncReturn => {
  const [modalDuration, setModalDuration] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [lastMediaId, setLastMediaId] = useState<string | null>(null);

  // Mídia atual
  const currentMedia = filteredMedias && filteredMedias.length > 0
    ? filteredMedias[currentMediaIndex]
    : null;

  const currentMediaTitle = currentMedia?.title || currentMedia?.name || 'Mídia sem título';
  const currentMediaId = currentMedia?.id || null;

  // Verificar se a mídia atual está nas mídias selecionadas
  const isCurrentMediaSelected = currentMedia && selectedMediaIds.length > 0
    ? selectedMediaIds.includes(currentMedia.id)
    : true; // Se não há mídias selecionadas, mostrar para todas

  // Reset quando a mídia mudar (otimizado para evitar loops)
  useEffect(() => {
    // Só processar se a mídia realmente mudou
    if (currentMediaId === lastMediaId) {
      return;
    }

    setLastMediaId(currentMediaId);

    if (!isLinkedMode || !currentMedia) {
      setIsActive(false);
      setModalDuration(0);
      setSecondsLeft(0);
      setProgress(0);
      setStartTime(null);
      return;
    }

    // VALIDAÇÃO CRÍTICA: Verificar se a mídia atual está selecionada
    if (!isCurrentMediaSelected) {
      console.log(`\n🔗 [LINKED SYNC] ❌ MÍDIA NÃO SELECIONADA`);
      console.log(`🔗 [LINKED SYNC] Mídia atual: '${currentMediaTitle}' (ID: ${currentMedia.id})`);
      console.log(`🔗 [LINKED SYNC] Mídias configuradas na promoção:`, selectedMediaIds);
      console.log(`🔗 [LINKED SYNC] Total de mídias selecionadas: ${selectedMediaIds.length}`);
      console.log(`🔗 [LINKED SYNC] ⚠️ FECHANDO MODAL - a promoção só deve ser exibida nas mídias selecionadas\n`);

      setIsActive(false);
      setModalDuration(0);
      setSecondsLeft(0);
      setProgress(0);
      setStartTime(null);

      // Notificar que a mídia não está selecionada
      if (onMediaNotSelected) {
        onMediaNotSelected();
      }

      return;
    }

    // Calcular duração da mídia atual
    const mediaDisplayTime = currentMedia.display_time || 5000; // Default 5 segundos
    const durationInSeconds = Math.max(Math.floor(mediaDisplayTime / 1000), 1); // Mínimo 1 segundo

    console.log(`\n🔗 [LINKED SYNC] ✅ MÍDIA SELECIONADA`);
    console.log(`🔗 [LINKED SYNC] Sincronizando com  mídia: '${currentMediaTitle}' (ID: ${currentMedia.id})`);
    console.log(`🔗 [LINKED SYNC] Tempo da mídia: ${mediaDisplayTime}ms (${durationInSeconds}s)`);
    console.log(`🔗 [LINKED SYNC] Índice da mídia: ${currentMediaIndex}\n`);

    // Configurar duração do modal baseada na mídia
    setModalDuration(durationInSeconds);
    setSecondsLeft(durationInSeconds);
    setProgress(0);
    setIsActive(true);
    setStartTime(Date.now());

  }, [isLinkedMode, currentMediaId, currentMedia, currentMediaTitle, isCurrentMediaSelected, selectedMediaIds, onMediaNotSelected, lastMediaId, currentMediaIndex]);

  // Timer para atualizar contador e progresso
  useEffect(() => {
    if (!isActive || !isLinkedMode || modalDuration === 0) {
      return;
    }

    console.log(`🔗 [LINKED SYNC] ⏱️ Iniciando timer para ${modalDuration} segundos`);

    const interval = setInterval(() => {
      if (!startTime) return;

      const elapsed = (Date.now() - startTime) / 1000;
      const remaining = Math.max(0, modalDuration - elapsed);
      const progressPercent = Math.min(1, elapsed / modalDuration);

      setSecondsLeft(Math.ceil(remaining));
      setProgress(progressPercent);

      // Modal expira quando a mídia terminar
      if (remaining <= 0) {
        console.log(`🔗 [LINKED SYNC] ⏰ Timer expirado para mídia '${currentMediaTitle}' - modal será fechado`);
        setIsActive(false);

        // Notificar que o tempo do modal expirou
        if (onModalTimeExpired) {
          onModalTimeExpired();
        }
      }
    }, 500); // Reduzir frequência para 500ms

    return () => {
      console.log('🔗 [LINKED SYNC] 🧹 Limpando timer');
      clearInterval(interval);
    };
  }, [isActive, isLinkedMode, modalDuration, startTime, currentMediaTitle, onModalTimeExpired]);

  // Função para forçar reset (útil para debug ou mudanças manuais)
  const resetSync = useCallback(() => {
    console.log('🔗 [LINKED SYNC] 🔄 Reset forçado');
    setIsActive(false);
    setStartTime(null);
  }, []);

  // Logs de estado para debug (reduzidos)
  useEffect(() => {
    if (isLinkedMode && isActive && secondsLeft % 5 === 0 && secondsLeft > 0) {
      console.log('🔗 [LINKED SYNC] 📊 Estado atual:', {
        modalDuration,
        secondsLeft,
        progress: Math.round(progress * 100) + '%',
        currentMedia: currentMediaTitle,
        isActive
      });
    }
  }, [isLinkedMode, isActive, modalDuration, secondsLeft, progress, currentMediaTitle]);

  return {
    modalDuration,
    secondsLeft,
    progress,
    isActive,
    currentMediaTitle
  };
};